var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import * as rootActions from '../../containers/Root/actions';
import * as rootSelectors from '../../containers/Root/selectors';
import toast from '../Toast';
import { injectApi401Callback, injectApi403Callback, injectApiGenericCallback, } from '../../services';
import { ERROR_BOUNDARY } from './constants';
var ErrorBoundary = /** @class */ (function (_super) {
    __extends(ErrorBoundary, _super);
    function ErrorBoundary(props) {
        var _this = _super.call(this, props) || this;
        _this.customError = null;
        _this.initSentry = function () {
            Sentry.init({
                dsn: 'https://2cb1cf34b3db40d28d55dc7ddbd09493@o380375.ingest.sentry.io/5206144',
                environment: process.env.APP_ENV,
            });
        };
        _this.closeSentry = function () {
            if (['production', 'staging', 'dev'].includes(process.env.APP_ENV)) {
                Sentry.close();
            }
        };
        _this.sentryManualLog = function (err) {
            if (['production', 'staging', 'dev'].includes(process.env.APP_ENV)) {
                Sentry.captureException(err);
            }
        };
        _this.resetError = function (cb) {
            if (cb === void 0) { cb = function () { return null; }; }
            _this.setState({ hasError: false, error: ERROR_BOUNDARY.OK }, cb);
        };
        _this.set401Error = function (customError) {
            if (customError === void 0) { customError = null; }
            _this.customError = customError;
            if (!!localStorage.getItem('token')) {
                _this.setState({
                    hasError: true,
                    error: customError || ERROR_BOUNDARY.TOKEN_EXPIRED,
                });
            }
        };
        _this.set403Error = function (customError) {
            if (customError === void 0) { customError = null; }
            _this.customError = customError;
            _this.setState({
                hasError: true,
                error: customError || ERROR_BOUNDARY.PERMISSIONS_ERROR,
            });
        };
        _this.setGenericError = function (customError) {
            if (customError === void 0) { customError = null; }
            _this.customError = customError;
            _this.setState({
                hasError: true,
                error: customError || ERROR_BOUNDARY.APP_CRASH,
            });
        };
        _this.reload = function () {
            if (_this.props.onReload) {
                _this.props.onReload().then(function () {
                    _this.resetError();
                });
            }
        };
        if (['production', 'staging', 'dev'].includes(process.env.APP_ENV)) {
            _this.initSentry();
        }
        _this.state = { hasError: false, error: ERROR_BOUNDARY.OK };
        return _this;
    }
    ErrorBoundary.prototype.componentDidMount = function () {
        this.ejectApi401Callback = injectApi401Callback(this.set401Error);
        this.ejectApi403Callback = injectApi403Callback(this.set403Error);
        this.ejectApiGenericCallback = injectApiGenericCallback(this.setGenericError);
    };
    // eslint-disable-next-line no-unused-vars
    ErrorBoundary.prototype.componentDidUpdate = function (prevProps, prevState, _) {
        var _this = this;
        if (this.state.hasError &&
            this.state.error === ERROR_BOUNDARY.TOKEN_EXPIRED &&
            prevProps.location.pathname !== '/token/expired') {
            this.resetError(function () {
                localStorage.removeItem('token');
                _this.props.goToTokenExpired(prevProps.location.pathname);
            });
        }
        if (!prevState.hasError &&
            this.state.error === ERROR_BOUNDARY.PERMISSIONS_ERROR) {
            this.resetError(function () {
                toast.error("You don't have enough permissions to do this.");
            });
        }
        if (!prevState.hasError && this.state.error === this.customError) {
            this.resetError(function () { });
            toast.error(this.customError);
            this.customError = null;
        }
    };
    ErrorBoundary.prototype.componentWillUnmount = function () {
        if (this.ejectApi401Callback) {
            this.ejectApi401Callback();
        }
        if (this.ejectApi403Callback) {
            this.ejectApi403Callback();
        }
        if (this.ejectApiGenericCallback) {
            this.ejectApiGenericCallback();
        }
        this.closeSentry();
    };
    ErrorBoundary.prototype.componentDidCatch = function (error, errorInfo) {
        console.error(error);
        console.error(errorInfo);
        this.sentryManualLog(error);
    };
    ErrorBoundary.getDerivedStateFromError = function () {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error: ERROR_BOUNDARY.APP_CRASH };
    };
    ErrorBoundary.prototype.render = function () {
        if (this.state.hasError && this.state.error === ERROR_BOUNDARY.APP_CRASH) {
            // You can render any custom fallback UI
            return (_jsx("div", __assign({ style: {
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }, onClick: this.reload }, { children: _jsx("div", { children: "Oops, something went wrong. Please click here to return to the login screen." }) })));
        }
        return this.props.children;
    };
    return ErrorBoundary;
}(React.Component));
var mapStateToProps = function (state) { return ({
    router: rootSelectors.selectRouter(state),
}); };
var mapDispatchToProps = function (dispatch, ownProps) {
    var navigate = ownProps.navigate;
    return {
        goToTokenExpired: function (pathname) {
            return dispatch(rootActions.logout("/token/expired?".concat(pathname), navigate));
        },
    };
};
var withConnect = connect(mapStateToProps, mapDispatchToProps);
var ErrorBoundaryPageWrapper = function (props) {
    var navigate = useNavigate();
    var location = useLocation();
    return (_jsx(ConnectedErrorBoundaryPage, __assign({ navigate: navigate, location: location }, props)));
};
var ConnectedErrorBoundaryPage = compose(withConnect)(ErrorBoundary);
export default ErrorBoundaryPageWrapper;
